import { SPECIAL_CASES_EUR } from "@/core/constants/language-and-currency";
import { ORDERS_QUOTES_STATUSES, EUS_DOCUSIGN_INDIA, EUS_DOCUSIGN_SENDER_NAMES } from "@/core/constants/orders";
import { globals } from "@/core/globals";
export {
  EUS_DOCUSIGN_CURRENCIES,
  EUS_DOCUSIGN_INDIA,
  EUS_DOCUSIGN_SENDER_EMAIL,
  EUS_DOCUSIGN_SENDER_NAMES,
} from "@/core/constants/orders";
import type {
  CartConfigurationItemTypeTl,
  CustomerOrderTypeExtension,
  LineItemTypeExtension,
  OrderConfigurationItemTypeExtension,
  OrderLineItemTypeExtension,
  OrderPaymentMethodType,
  OrderShipmentTypeExtension,
  PaymentMethodType,
  ShipmentTypeExtension,
} from "@/core/api/graphql/types";
import type { EndUserStatementLinksType, HeavyweightFormLinksType } from "@/shared/account";
import type { LineItemWithAvailabilityStatus, OrderLineItemWithAvailabilityStatus } from "@/shared/cart";
import type {
  CheckoutItemType,
  CheckoutPaymentMethodType,
  CheckoutShipmentType,
  ShippingChoiceType,
} from "@/shared/checkout";
import type { ComposerTranslation } from "vue-i18n";

export function isAcceptedQuote(status: string | undefined, erpQuoteIdTL: string | undefined): boolean {
  return status !== "Quote" && !!erpQuoteIdTL;
}
export function getOrderNumber(order: CustomerOrderTypeExtension | null, isQuote: boolean): string {
  if (!order) {
    return "";
  }
  const erpOrderNumber = isQuote ? order.erpQuoteIdTL : order.outerId;
  const firstSection = erpOrderNumber ? `${erpOrderNumber} / ` : "";
  return `${firstSection}${order.number}`;
}
export function getDaysUntilQuoteExpiration(createdDateString: string, orderCurrency: string) {
  const createdDate = new Date(createdDateString);
  const currentDate = new Date();
  const diffTime = currentDate.getTime() - createdDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const offset = globals.quoteExpiration[orderCurrency] ?? 30;
  return offset - diffDays;
}
export function getExpirationDate(createdDateString: string, orderCurrency: string) {
  const createdDate = new Date(createdDateString);
  const expirationDate = new Date(createdDate);
  const offset = globals.quoteExpiration[orderCurrency] ?? 30;
  expirationDate.setDate(expirationDate.getDate() + offset);
  return expirationDate;
}
export function getHeavyweightRequiredLabel(isQuote: boolean): string {
  return isQuote ? "pages.checkout.quote_heavyweight_required_label" : "pages.checkout.heavyweight_required_label";
}
export function getEusRequiredLabel(isQuote: boolean): string {
  return isQuote ? "pages.checkout.quote_eus_required_label" : "pages.checkout.eus_required_label";
}
export function getActionsRequiredLabel(isQuote: boolean): string {
  return isQuote ? "pages.checkout.quote_actions_required_label" : "pages.checkout.actions_required_label";
}
function getQuoteStatusForDetailsPage(
  isExpiredQuote: boolean,
  salesOrderStatusTL: string,
  status: string,
  erpOrderNumber: string,
): string {
  if (isExpiredQuote) {
    return ORDERS_QUOTES_STATUSES["expiredQuote"];
  }

  if (status === "Quote") {
    if (salesOrderStatusTL === "Created") {
      return ORDERS_QUOTES_STATUSES["requested"];
    }
    if (salesOrderStatusTL === "Sent") {
      return ORDERS_QUOTES_STATUSES["readyToAccept"];
    }
    return ORDERS_QUOTES_STATUSES["pending"];
  }
  if (salesOrderStatusTL === "Confirmed" && erpOrderNumber) {
    return ORDERS_QUOTES_STATUSES["ordered"];
  }
  if (status !== "Quote" && salesOrderStatusTL === "Sent" && erpOrderNumber) {
    return ORDERS_QUOTES_STATUSES["placed"];
  }
  return ORDERS_QUOTES_STATUSES["pending"];
}
export function getStatusToDisplay(
  isQuoteRelatedStatus: boolean,
  status: string,
  isExpiredQuote: boolean,
  salesOrderStatusTL: string,
  erpOrderNumber: string,
): string {
  if (isQuoteRelatedStatus) {
    return getQuoteStatusForDetailsPage(isExpiredQuote, salesOrderStatusTL, status, erpOrderNumber);
  }
  if (status === "Delivered" || status === "Invoiced" || status === "Canceled") {
    return ORDERS_QUOTES_STATUSES["processed"];
  }
  if (status === "None" || status === "Backorder" || status === "Approved") {
    return ORDERS_QUOTES_STATUSES["inProcess"];
  }

  return status;
}

export function getInvoiceMeNotificationText(
  netTerms: string | undefined,
  t: ComposerTranslation,
  isTooltip?: boolean,
): string {
  const normalizedNetTerms = netTerms?.toUpperCase();

  switch (normalizedNetTerms) {
    case "N1":
    case "N2":
    case "N5":
    case "N7":
    case "N10":
    case "N15":
    case "N20":
    case "N25":
    case "N30":
    case "N35":
    case "N45":
    case "N50":
    case "N60":
    case "N75":
    case "N90":
    case "N120":
    case "N135":
    case "N180": {
      const termNumber = normalizedNetTerms.startsWith("N") ? parseInt(normalizedNetTerms.substring(1)) || 0 : 0;
      const days = termNumber > 1 ? t("common.labels.days") : t("common.labels.day");
      return t("pages.checkout.invoice_days_notification", [termNumber, days]);
    }
    case "N0":
    case "PRE":
    case "PAID":
    case "STOP":
    case "LOAN":
    case "LC":
    case "COD":
    case "CCARD":
    case "30EOM10":
    case "30EOM25":
    case "30EOM31":
    case "30EOM5":
    case "50% PRE 50% NET 30":
    case "80% UPON DELIVERY, 20% UPON ACCEPTANCE":
      return t("pages.checkout.invoice_contact_notification");
  }

  if (isTooltip) {
    return t("pages.checkout.invoice_tooltip_no_net_terms");
  }

  return t("pages.checkout.invoice_no_net_terms");
}

export function getPaymentNetTermsText(netTerms: string | undefined, t: ComposerTranslation): string {
  const normalizedNetTerms = netTerms?.toUpperCase();

  switch (normalizedNetTerms) {
    case "N1":
    case "N2":
    case "N5":
    case "N7":
    case "N10":
    case "N15":
    case "N20":
    case "N25":
    case "N30":
    case "N35":
    case "N45":
    case "N50":
    case "N60":
    case "N75":
    case "N90":
    case "N120":
    case "N135":
    case "N180": {
      const termNumber = normalizedNetTerms.startsWith("N") ? parseInt(normalizedNetTerms.substring(1)) || 0 : 0;
      const days = termNumber > 1 ? t("common.labels.days") : t("common.labels.day");
      return t("pages.account.order_payment.net_terms_days_notification", [termNumber, days]);
    }
    case "N0":
    case "PRE":
    case "PAID":
    case "LOAN":
    case "LC":
    case "COD":
    case "CCARD":
    case "30EOM10":
    case "30EOM25":
    case "30EOM31":
    case "30EOM5":
    case "50% PRE 50% NET 30":
    case "80% UPON DELIVERY, 20% UPON ACCEPTANCE":
      return t("pages.account.order_payment.net_terms_contact_notification");
    case "STOP":
      return t("pages.account.order_payment.net_terms_stop");
  }

  return t("pages.account.order_payment.net_terms_contact_notification");
}

export function getHeavyweightLinks(currencyCode: string, deliveryCountry: string) {
  const result = {} as HeavyweightFormLinksType;
  const heavyWeightSetting = globals.heavyWeightForm;

  if (heavyWeightSetting && heavyWeightSetting[currencyCode]) {
    // Get the country settings for the current currency
    const currencySettings = heavyWeightSetting[currencyCode];

    // Try to find settings for the specific country code
    if (deliveryCountry && currencySettings[deliveryCountry]) {
      const countrySettings = currencySettings[deliveryCountry];

      if (countrySettings.pdf) {
        result.heavyweight_pdf = countrySettings.pdf;
      }

      if (countrySettings.docusign) {
        result.heavyweight_docusign = countrySettings.docusign;
      }
    }
    // If no specific country settings found or delivery country is not provided, use default
    else if (currencySettings.default) {
      const defaultSettings = currencySettings.default;

      if (defaultSettings.pdf) {
        result.heavyweight_pdf = defaultSettings.pdf;
      }

      if (defaultSettings.docusign) {
        result.heavyweight_docusign = defaultSettings.docusign;
      }
    }
  }

  return result;
}

const nonEmpty = (v?: string | null) => (v && v.trim() ? v : undefined);

export function getEus(currencyCode: string, deliveryCountry?: string, isReseller?: boolean) {
  const result = {} as EndUserStatementLinksType;

  type EUSLinks = { pdf?: string | null; docusign?: string | null };
  type CurrencySettings = { default?: EUSLinks } & Record<string, EUSLinks | undefined>;

  // If reseller, try reseller-specific EUS first
  if (isReseller) {
    const resellerSettingsByCurrency = globals.resellerEndUserStatements as Record<string, CurrencySettings>;
    const resellerCurrencySettings = resellerSettingsByCurrency?.[currencyCode];
    if (resellerCurrencySettings) {
      const resellerResult = resolveEusFromSettings(resellerCurrencySettings, deliveryCountry);
      if (resellerResult.eus_pdf || resellerResult.eus_docusign) {
        return resellerResult;
      }
    }
  }

  // Fallback to standard EUS
  const settingsByCurrency = globals.endUserStatements as Record<string, CurrencySettings>;
  const currencySettings = settingsByCurrency?.[currencyCode];
  if (!currencySettings) {
    return result;
  }

  return resolveEusFromSettings(currencySettings, deliveryCountry);
}

function resolveEusFromSettings(
  currencySettings: { default?: { pdf?: string | null; docusign?: string | null } } & Record<
    string,
    { pdf?: string | null; docusign?: string | null } | undefined
  >,
  deliveryCountry?: string,
) {
  const result = {} as EndUserStatementLinksType;

  const def = currencySettings.default ?? {};
  const defPdf = nonEmpty(def.pdf);
  const defDocusign = nonEmpty(def.docusign);

  //Initialize with default links
  if (defPdf) {
    result.eus_pdf = defPdf;
  }
  if (defDocusign) {
    result.eus_docusign = defDocusign;
  }

  //Overwrite with country-specific links if available
  if (deliveryCountry) {
    const countrySetting = currencySettings[deliveryCountry];
    if (countrySetting) {
      const cPdf = nonEmpty(countrySetting.pdf);
      const cDocusign = nonEmpty(countrySetting.docusign);
      if (cPdf) {
        result.eus_pdf = cPdf;
      }
      if (cDocusign) {
        result.eus_docusign = cDocusign;
      }
    }
  }
  return result;
}

export function isIndiaEusDocusign(
  deliveryCountry?: string,
  inrCurrencyEnabled: boolean = !!globals.inrCurrencyEnabled,
): boolean {
  return inrCurrencyEnabled && deliveryCountry?.toUpperCase() === EUS_DOCUSIGN_INDIA.countryCode;
}

export function getEusDocusignSenderName(currencyCode: string, deliveryCountry?: string): string {
  if (currencyCode === "GBP") {
    return EUS_DOCUSIGN_SENDER_NAMES.GBP;
  }
  if (currencyCode === "SEK") {
    return EUS_DOCUSIGN_SENDER_NAMES.SEK;
  }
  if (currencyCode === "EUR") {
    if (deliveryCountry && SPECIAL_CASES_EUR.FRA.includes(deliveryCountry)) {
      return EUS_DOCUSIGN_SENDER_NAMES.EUR.FRA;
    }
    if (deliveryCountry && SPECIAL_CASES_EUR.SWE.includes(deliveryCountry)) {
      return EUS_DOCUSIGN_SENDER_NAMES.EUR.SWE;
    }
    return EUS_DOCUSIGN_SENDER_NAMES.EUR.default;
  }
  return "";
}

function getListPrice(lineItem: LineItemTypeExtension | OrderLineItemTypeExtension, isCustomerOrder: boolean) {
  return isCustomerOrder && "price" in lineItem && lineItem.price
    ? lineItem.price
    : (lineItem as LineItemTypeExtension).listPrice;
}

export function approximateBusinessDays(days: string, t: ComposerTranslation) {
  return days == "1" ? `${days} ${t("shared.checkout.business_day")}` : `${days} ${t("shared.checkout.business_days")}`;
}

export function getTransitTimeDisplay(choice: ShippingChoiceType, t: ComposerTranslation): string {
  if (!choice) {
    return "";
  }

  // Case 1: transitTimeText is populated
  if (choice.transitTimeText) {
    // Check if there's a mapping in globals.shippingTransitTimeText
    if (choice.transitTimeText in globals.shippingTransitTimeText) {
      const mappedText = globals.shippingTransitTimeText[choice.transitTimeText];
      // If the mapping exists but is empty, don't display anything
      if (mappedText === "") {
        return "";
      }
      // Otherwise, use the mapped text
      return mappedText;
    }
    // If no mapping exists, display the original text
    return choice.transitTimeText;
  }

  // Case 2: minimumTransitTimeInDays is populated but maximumTransitTimeInDays is not
  if (choice.minimumTransitTimeInDays && !choice.maximumTransitTimeInDays) {
    return approximateBusinessDays(choice.minimumTransitTimeInDays, t);
  }

  // Case 3: Both minimumTransitTimeInDays and maximumTransitTimeInDays are populated
  if (choice.minimumTransitTimeInDays && choice.maximumTransitTimeInDays) {
    return `${choice.minimumTransitTimeInDays}-${choice.maximumTransitTimeInDays} ${t("shared.checkout.business_days")}`;
  }

  return "";
}

// useCheckoutFacade.ts utilities
export function mapToCheckoutItems(
  items: Array<LineItemWithAvailabilityStatus>,
  isCustomerOrder: boolean,
): CheckoutItemType[] {
  return items.map((item) => {
    const lineItem = item.lineItem as LineItemTypeExtension;
    return {
      lineItem: {
        id: lineItem.id,
        name: lineItem.name || "",
        imageUrl: lineItem.imageUrl || "",
        quantity: lineItem.quantity,
        sku: lineItem.sku,
        itemNumberTL: lineItem.itemNumberTL,
        serialNumberTL: lineItem.serialNumberTL,
        customerReferenceTL: lineItem.customerReferenceTL,
        customDescriptionTL: lineItem.customDescriptionTL,
        configurationItemsTL: (lineItem.configurationItemsTL ?? undefined) as
          | Array<CartConfigurationItemTypeTl>
          | undefined,
        placedPrice: lineItem.placedPrice,
        listPrice: getListPrice(lineItem, isCustomerOrder),
        listPriceAdaptedToQuantityTL: lineItem.listPriceAdaptedToQuantityTL,
        extendedPrice: lineItem.extendedPrice,
        listPriceWithTaxAdaptedToQuantityTL: lineItem.listPriceWithTaxAdaptedToQuantityTL,
        extendedPriceWithTax: lineItem.extendedPriceWithTax,
        discounts: lineItem.discounts,
        product: {
          mainProductId: lineItem.product?.mainProductId,
          code: lineItem.product?.code,
          isCalibrationServiceTL: lineItem.product?.isCalibrationServiceTL,
          isConfigurable: lineItem.product?.isConfigurable,
          descriptionsWithFallbacksTL: lineItem.product?.descriptionsWithFallbacksTL?.map((description) => ({
            reviewType: description.reviewType,
            content: description.content,
          })),
          masterVariation: {
            slug: lineItem.product?.masterVariation?.slug,
            images: lineItem.product?.masterVariation?.images?.map((img) => ({ name: img.name, url: img.url })),
            descriptionsWithFallbacksTL: lineItem.product?.masterVariation?.descriptionsWithFallbacksTL?.map(
              (description) => ({
                reviewType: description.reviewType,
                content: description.content,
              }),
            ),
          },
          associationsTL: lineItem.product?.associationsTL,
        },
        accessoryIdTL: lineItem.accessoryIdTL,
      },
      status: item.status,
      productTitle: item.productTitle,
    };
  });
}

export function mapToCheckoutItemsFromOrder(
  items: Array<OrderLineItemWithAvailabilityStatus>,
  isCustomerOrder: boolean,
): CheckoutItemType[] {
  return items.map((item) => {
    const lineItem = item.lineItem as OrderLineItemTypeExtension;
    return {
      lineItem: {
        id: lineItem.id,
        name: lineItem.name || "",
        imageUrl: lineItem.imageUrl || "",
        quantity: lineItem.quantity,
        sku: lineItem.sku,
        itemNumberTL: lineItem.itemNumberTL,
        serialNumberTL: lineItem.serialNumberTL,
        customerReferenceTL: lineItem.customerReferenceTL,
        customDescriptionTL: lineItem.customDescriptionTL,
        configurationItems: (lineItem.configurationItems ?? undefined) as
          | Array<OrderConfigurationItemTypeExtension>
          | undefined,
        placedPrice: lineItem.placedPrice,
        listPrice: getListPrice(lineItem, isCustomerOrder),
        listPriceAdaptedToQuantityTL: lineItem.listPriceAdaptedToQuantityTL,
        extendedPrice: lineItem.extendedPrice,
        listPriceWithTaxAdaptedToQuantityTL: lineItem.listPriceWithTaxAdaptedToQuantityTL,
        extendedPriceWithTax: lineItem.extendedPriceWithTax,
        discounts: lineItem.discounts,
        product: {
          mainProductId: lineItem.product?.mainProductId,
          code: lineItem.product?.code,
          isCalibrationServiceTL: lineItem.product?.isCalibrationServiceTL,
          isConfigurable: lineItem.product?.isConfigurable,
          imgSrc: lineItem.product?.imgSrc,
          images: lineItem.product?.images?.map((img) => ({ url: img.url })),
          masterVariation: {
            slug: lineItem.product?.masterVariation?.slug,
            images: lineItem.product?.masterVariation?.images?.map((img) => ({ name: img.name, url: img.url })),
          },
          associationsTL: lineItem.product?.associationsTL,
        },
        accessoryIdTL: lineItem.accessoryIdTL,
      },
      status: item.status,
      productTitle: item.productTitle,
    };
  });
}

export function mapToCheckoutShipment(
  shipment: ShipmentTypeExtension | OrderShipmentTypeExtension | undefined | null,
): CheckoutShipmentType {
  return {
    id: shipment?.id ?? "",
    shipmentMethodCode: shipment?.shipmentMethodCode ?? "",
    shipmentPriceAmount: shipment?.price?.amount ?? 0,
    shippingAccountNumberTL: shipment?.shippingAccountNumberTL,
    shipmentMethodOption: shipment?.shipmentMethodOption,
    hasDeliveryAddress: !!shipment?.deliveryAddress,
    businessDaysToShipTL: shipment?.businessDaysToShipTL,
    wantsFewerPackagesTL: shipment?.wantsFewerPackagesTL ?? false,
  };
}

export function mapToCheckoutPaymentMethod(
  paymentMethod: PaymentMethodType[] | OrderPaymentMethodType[],
): CheckoutPaymentMethodType[] {
  return paymentMethod.map((method) => ({
    code: method.code,
    name: method.name || "",
  }));
}
